import type { OwnCatalogEntry, SharedCatalogEntry } from "../../shared/api/requests";

/** One choosable destination in the create form's entry picker. */
export interface CreateEntryChoice {
  id: string;
  /** `name` for own entries, `name@owner` for shared ones (ticket 05). */
  label: string;
  /** Owner name when the entry is shared; undefined for own entries. */
  owner?: string;
}

/** The resolved create target: the writable choices plus the chosen entry. */
export interface CreateEntryTarget {
  /** Entries the caller may create roots in. */
  list: CreateEntryChoice[];
  /** The resolved target entry id (undefined when no writable entry exists). */
  entryId?: string;
  /** True when the current selection was unusable and the default was substituted. */
  fallback: boolean;
}

/**
 * Resolve where a new root container should land (container-catalog-transfer
 * 04/05). The chosen entry defaults to the currently selected catalog entry
 * when it is writable; otherwise the caller's default own entry is used and
 * `fallback` is set so the form can explain the substitution.
 *
 * Ticket 05 extends `list` with shared entries held at `write` or `admin`,
 * rendered `name@owner` so they are distinguishable from own entries, and
 * treats them as valid selection targets too. Read-only shared entries are
 * never offered (their selection falls back to the default own entry).
 */
export function resolveCreateEntry(
  entries: OwnCatalogEntry[],
  sharedWithMe: SharedCatalogEntry[],
  selectedEntryId?: string,
): CreateEntryTarget {
  const list: CreateEntryChoice[] = [
    ...entries.map((entry) => ({ id: entry.id, label: entry.name })),
    ...sharedWithMe
      .filter((shared) => shared.level === "write" || shared.level === "admin")
      .map((shared) => ({
        id: shared.entry.id,
        label: `${shared.entry.name}@${shared.owner}`,
        owner: shared.owner,
      })),
  ];
  const writable = new Set(list.map((choice) => choice.id));

  if (selectedEntryId !== undefined && writable.has(selectedEntryId)) {
    return { list, entryId: selectedEntryId, fallback: false };
  }

  const fallbackEntry = entries.find((entry) => entry.isDefault) ?? entries[0];
  return {
    list,
    entryId: fallbackEntry?.id,
    fallback: selectedEntryId !== undefined,
  };
}
